import { useState, useRef, useEffect } from 'react'
import { Icon } from '@iconify/react'

/**
 * Reusable context menu component
 * @param {Object} props - Component props
 * @param {Array} props.menuItems - Array of menu items with label, onClick, and className
 * @param {string} props.iconName - Optional custom icon name (default: mdi:dots-vertical)
 * @param {string} props.iconClassName - Optional custom icon class
 * @param {string} props.buttonClassName - Optional custom button class
 * @param {string} props.menuClassName - Optional custom menu container class
 * @param {string} props.menuItemClassName - Optional custom menu item class
 */
export default function ContextMenu({
  menuItems = [],
  iconName = 'mdi:dots-vertical',
  iconClassName = 'text-gray-500 hover:text-gray-700',
  buttonClassName = '',
  menuClassName = '',
  menuItemClassName = '',
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const buttonRef = useRef(null)

  useEffect(() => {
    // Close menu when clicking outside
    const handleClickOutside = (event) => {
      // Don't close if clicking the button - let the button handler handle that
      if (buttonRef.current && buttonRef.current.contains(event.target)) {
        return
      }
      
      // Close if clicking outside the menu
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const toggleMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsMenuOpen(!isMenuOpen)
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        className={`${iconClassName} ${buttonClassName}`}
        onClick={toggleMenu}
        aria-label="Menu"
      >
        <Icon icon={iconName} width="1.2rem" />
      </button>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className={`absolute right-0 z-10 mt-1 w-40 rounded-md border bg-white shadow-lg ${menuClassName}`}
        >
          {menuItems.map((item, index) => (
            <button
              key={index}
              className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                index < menuItems.length - 1 ? 'border-b' : ''
              } ${item.className || ''} ${menuItemClassName}`}
              onClick={(e) => {
                // Prevent default behavior and stop propagation
                e.preventDefault()
                e.stopPropagation()
                item.onClick && item.onClick()
                setIsMenuOpen(false)
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}